import React from 'react';
import { IntegratedAnalysisResult, SrmProject, SrmModule, EventSecurityPlanResult } from '@types';
import { ICONS, md, Section } from './ReportShared';

interface SrmAnalysisDisplayProps {
    result: IntegratedAnalysisResult | EventSecurityPlanResult;
    srmProject?: SrmProject;
    srmModule?: SrmModule;
}

const SrmAnalysisDisplay: React.FC<SrmAnalysisDisplayProps> = ({ result, srmProject, srmModule }) => {
    return (
        <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-1000">
            {/* 1. Executive Summary */}
            <Section title="Executive Intelligence Overview">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-indigo-500/20 transition-all duration-1000"></div>
                    <div className="relative z-10">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="bg-indigo-600 p-3 rounded-2xl shadow-lg shadow-indigo-500/20">{ICONS.chart}</div>
                            <div>
                                <h3 className="text-2xl font-black tracking-tight">Strategic Summary</h3>
                                <p className="text-indigo-400 text-[10px] font-bold uppercase tracking-widest">SRM // Integrated Intelligence Architecture</p>
                            </div>
                        </div>
                        <div className="prose prose-invert prose-sm max-w-none leading-relaxed text-slate-300" dangerouslySetInnerHTML={{ __html: md.render((result as any).executiveSummary || 'Executive summary pending tactical synthesis.') }} />
                    </div>
                </div>
            </Section>

            {/* 2. Project Context (if available) */}
            {srmProject && (
                <Section title="Operational Context">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                            <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Location Matrix</h5>
                            <p className="text-lg font-black text-slate-900">{srmProject.locationName}</p>
                            <p className="text-xs text-slate-500 mt-1">{(srmProject as any).sector || 'Global Operations'}</p>
                        </div>
                        {srmModule && (
                            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Active Module</h5>
                                <p className="text-lg font-black text-slate-900">{(srmModule as any).name || srmModule.title}</p>
                                <p className="text-xs text-slate-500 mt-1">Version {(srmModule as any).version || '1.0'}</p>
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* 3. Localized Threat Analysis */}
            <Section title="Localized Threat Analysis">
                <div className="space-y-8">
                    <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 text-slate-200 group-hover:text-indigo-100 transition-colors">{ICONS.chart}</div>
                        <div className="prose prose-sm text-slate-700 max-w-none leading-relaxed italic" dangerouslySetInnerHTML={{ __html: md.render((result as any).localizedThreatAnalysis?.summary || 'Localized analysis pending sector sweep.') }} />
                    </div>
                    
                    {((result as any).localizedThreatAnalysis?.localRisks) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {(result as any).localizedThreatAnalysis.localRisks.map((risk: any, idx: number) => (
                                <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:scale-[1.02] transition-all">
                                    <div className={`absolute top-0 right-0 w-16 h-16 -mr-4 -mt-4 rotate-12 flex items-center justify-center opacity-5 group-hover:opacity-10 transition-opacity ${
                                        risk.level === 'High' ? 'text-red-600' : risk.level === 'Medium' ? 'text-amber-600' : 'text-emerald-600'
                                    }`}>
                                        <div className="scale-[3]">{ICONS.alert}</div>
                                    </div>
                                    <h5 className="font-bold text-slate-900 flex items-center gap-2 mb-3">
                                        <span className={`w-2 h-2 rounded-full ${
                                            risk.level === 'High' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' : risk.level === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500'
                                        }`}></span>
                                        {risk.risk}
                                    </h5>
                                    <p className="text-xs text-slate-500 leading-relaxed line-clamp-3">{risk.description}</p>
                                    <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-100">
                                        <span className="text-[10px] font-black uppercase tracking-tighter text-slate-400">Impact</span>
                                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                                            risk.level === 'High' ? 'bg-red-50 text-red-600' : risk.level === 'Medium' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                                        }`}>{risk.level}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Section>

            {/* 4. Risk Management Plan */}
            <Section title="Security Risk Management Plan">
                <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-800">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-800/50">
                                    <th className="px-6 py-4 text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">Asset/Area</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">Primary Threat</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">Risk Level</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em]">Mitigation Strategy</th>
                                </tr>
                            </thead>
                            <tbody className="text-xs text-slate-300 font-sans">
                                {(result as any).securityRiskManagementPlan?.riskMatrix && Array.isArray((result as any).securityRiskManagementPlan.riskMatrix) && (result as any).securityRiskManagementPlan.riskMatrix.map((item: any, idx: number) => (
                                    <tr key={idx} className="hover:bg-slate-800/30 transition-colors border-b border-slate-900/50 last:border-0">
                                        <td className="px-6 py-4 font-bold text-slate-100 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                                            {item.asset}
                                        </td>
                                        <td className="px-6 py-4 text-slate-400">{item.threat}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-grow h-1.5 bg-slate-800 rounded-full overflow-hidden w-16">
                                                    <div className={`h-full rounded-full ${
                                                        item.score > 70 ? 'bg-red-500' : item.score > 40 ? 'bg-amber-500' : 'bg-emerald-500'
                                                    }`} style={{ width: `${item.score}%` }}></div>
                                                </div>
                                                <span className="font-black text-[10px]">{item.score}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 leading-relaxed italic text-indigo-200/80">{item.mitigation}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mt-6 prose prose-sm text-slate-500 italic px-6 border-l-4 border-indigo-500/30 bg-indigo-50/20 py-4 rounded-r-xl" dangerouslySetInnerHTML={{ __html: md.render((result as any).securityRiskManagementPlan?.methodology || 'Methodology based on adaptive threat modeling.') }} />
            </Section>

            {/* 5. Access Control & Accreditation */}
            {((result as any).accessControlPlan) && (
                <Section title="Access Control & Accreditation">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 hover:border-indigo-300 transition-colors">
                            <h5 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-4">Perimeter Strategy</h5>
                            <p className="text-sm text-slate-600 leading-relaxed">{(result as any).accessControlPlan.perimeterSecurity}</p>
                        </div>
                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 hover:border-indigo-300 transition-colors">
                            <h5 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-4">Screening Protocols</h5>
                            <p className="text-sm text-slate-600 leading-relaxed">{(result as any).accessControlPlan.screeningProcedures}</p>
                        </div>
                        <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 hover:border-indigo-300 transition-colors">
                            <h5 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-4">Credentialing System</h5>
                            <p className="text-sm text-slate-600 leading-relaxed">{(result as any).accessControlPlan.accreditationSystem}</p>
                        </div>
                    </div>
                </Section>
            )}

            {/* 6. Health & Safety (HSSE) */}
            {((result as any).healthAndSafetyPlan) && (
                <Section title="HSSE & Health Protocols">
                    <div className="bg-emerald-50/30 border border-emerald-100 rounded-3xl p-8 shadow-sm">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="bg-emerald-600 text-white p-3 rounded-2xl shadow-lg shadow-emerald-200">{ICONS.health}</div>
                            <div>
                                <h4 className="text-xl font-black text-slate-900 tracking-tight">Environmental Health & Life Safety</h4>
                                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">OSHA // WHO Standard Compliance</p>
                            </div>
                        </div>
                        
                        <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-emerald-100 shadow-sm">
                            <div className="prose prose-sm text-slate-700 max-w-none leading-relaxed text-base" dangerouslySetInnerHTML={{ __html: md.render((result as any).healthAndSafetyPlan.summary) }} />
                            
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {((result as any).healthAndSafetyPlan?.keyProcedures || []).map((proc: any, idx: number) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 shadow-sm group">
                                        <span className="w-8 h-8 flex items-center justify-center bg-white text-emerald-600 rounded-lg text-xs font-black shadow-sm group-hover:bg-emerald-600 group-hover:text-white transition-all">{idx + 1}</span>
                                        <span className="text-xs font-bold text-emerald-900 uppercase tracking-tight">{proc}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8 p-5 bg-emerald-900 text-white rounded-2xl flex items-center justify-between shadow-xl">
                            <div className="flex items-center gap-4">
                                <span className="w-10 h-10 flex items-center justify-center bg-emerald-800 rounded-xl text-lg">{ICONS.shield}</span>
                                <span className="text-xs font-bold uppercase tracking-widest leading-tight">Compliance & Regulatory Matrix</span>
                            </div>
                            <div className="text-right">
                                <p className="text-[9px] font-bold text-emerald-400 uppercase mb-1">Status</p>
                                <p className="text-sm font-black text-emerald-100 italic">{(result as any).healthAndSafetyPlan.complianceNotes}</p>
                            </div>
                        </div>
                    </div>
                </Section>
            )}

            {/* 7. Executive Protection Detail */}
            {((result as any).epDetail) && (
                <Section title="Executive Protection Detail">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800 shadow-xl group hover:border-blue-500/50 transition-colors">
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/20">{ICONS.users}</div>
                            <h5 className="font-black text-white text-xs uppercase tracking-[0.2em] mb-4">Principal Safety</h5>
                            <p className="text-sm text-slate-400 leading-relaxed">{(result as any).epDetail.principalProtection}</p>
                        </div>
                        <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800 shadow-xl group hover:border-blue-500/50 transition-colors">
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/20">{ICONS.map}</div>
                            <h5 className="font-black text-white text-xs uppercase tracking-[0.2em] mb-4">Movement Strategy</h5>
                            <p className="text-sm text-slate-400 leading-relaxed">{(result as any).epDetail.movementPlan}</p>
                        </div>
                        <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800 shadow-xl group hover:border-blue-500/50 transition-colors">
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-blue-500/20">{ICONS.alert}</div>
                            <h5 className="font-black text-white text-xs uppercase tracking-[0.2em] mb-4">Safe Haven Protocol</h5>
                            <p className="text-sm text-slate-400 leading-relaxed">{(result as any).epDetail.safeHavens}</p>
                        </div>
                    </div>
                </Section>
            )}

            {/* 8. Staffing & Admin */}
            {((result as any).staffingAndAdmin) && (
                <Section title="Staffing & Operational Administration">
                    <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                        <div className="p-8 border-b border-slate-100 bg-slate-50/50">
                            <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Administrative Mandate</h5>
                            <p className="text-slate-600 leading-relaxed font-medium">{(result as any).staffingAndAdmin.adminRequirements}</p>
                        </div>
                        <div className="p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-900 text-[10px] uppercase font-black text-slate-500 tracking-[0.2em]">
                                    <tr>
                                        <th className="px-8 py-5 border-b border-slate-800">Operational Role</th>
                                        <th className="px-8 py-5 border-b border-slate-800 text-center">Unit Count</th>
                                        <th className="px-8 py-5 border-b border-slate-800">Primary Mission Responsibility</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm">
                                    {((result as any).staffingAndAdmin?.staffingTable || []).map((staff: any, idx: number) => (
                                        <tr key={idx} className="hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0">
                                            <td className="px-8 py-5 font-bold text-slate-900 flex items-center gap-3">
                                                <span className="w-2 h-2 bg-indigo-600 rounded-full shadow-sm"></span>
                                                {staff.role}
                                            </td>
                                            <td className="px-8 py-5 text-center">
                                                <span className="px-4 py-1.5 bg-slate-900 rounded-lg text-xs font-black text-white shadow-lg">{staff.quantity}</span>
                                            </td>
                                            <td className="px-8 py-5 text-slate-600 font-medium leading-relaxed">{staff.responsibility}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Section>
            )}

            {/* 9. Operational Logistics & C3 */}
            {((result as any).c3 || (result as any).logistics) && (
                <Section title="C3 & Operational Logistics">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {((result as any).c3) && (
                            <div className="space-y-6">
                                <div className="bg-slate-900 p-8 rounded-3xl border border-slate-800">
                                    <h5 className="text-blue-500 text-[10px] font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-3">
                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                                        Command & Control
                                    </h5>
                                    <div className="prose prose-invert prose-sm">
                                        <h6 className="text-white font-bold mb-2">Structure:</h6>
                                        <p className="text-slate-400">{(result as any).c3.commandStructure}</p>
                                        <h6 className="text-white font-bold mt-4 mb-2">Comms Plan:</h6>
                                        <p className="text-slate-400">{(result as any).c3.commsPlan}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {((result as any).logistics) && (
                            <div className="grid grid-cols-1 gap-4">
                                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                    <h6 className="text-[10px] font-black text-slate-400 uppercase mb-2">Equipment Matrix</h6>
                                    <p className="text-sm text-slate-700 font-medium">{(result as any).logistics.equipment}</p>
                                </div>
                                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                    <h6 className="text-[10px] font-black text-slate-400 uppercase mb-2">Medical Support</h6>
                                    <p className="text-sm text-slate-700 font-medium">{(result as any).logistics.medical}</p>
                                </div>
                                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200">
                                    <h6 className="text-[10px] font-black text-slate-400 uppercase mb-2">Agency Liaison</h6>
                                    <p className="text-sm text-slate-700 font-medium">{(result as any).logistics.liaison}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </Section>
            )}
            {/* 10. Additional Intelligence Annex */}
            {(result as any).additionalSections && (result as any).additionalSections.length > 0 && (
                <div className="space-y-10">
                    {(result as any).additionalSections.map((section: any, idx: number) => (
                        <Section key={idx} title={section.title || 'Intelligence Annex'}>
                            <div className="prose prose-sm text-slate-700 max-w-none leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} />
                        </Section>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SrmAnalysisDisplay;
